/**
 * VRG-691 — display helpers for the deals board, the lists and the drawer. The arithmetic is in
 * `@repo/types` (gtm/sales-pipeline); this is formatting and copy only.
 */
import {
  DEAL_PLAN_LABELS,
  DEAL_PLANS,
  DEAL_VALUE_BASIS_LABELS,
  EXPANSION_SOURCE,
  LEAD_SOURCE_GROUP_LABELS,
  SALES_BOARD_LANE_LABELS,
  SALES_STAGE_LABELS,
  FOUNDERS_LIST_STATUSES,
  FOUNDERS_LIST_STATUS_META,
  type DealValue,
  type LeadSourceGroup,
  type FoundersListStatus,
  type SalesBoardLane,
  type SalesStage,
} from '@repo/types'

import { formatMoneyCentsCompact } from 'components/analytics/analyticsMoney'
import type { ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { SOURCE_CHANNEL_OPTIONS, sourceChannelLabel } from 'constants/sourceChannels'
import type { SelectItem } from 'components/input/fields/SelectField'
import type { SalesDealCard, SalesLeadSource } from 'services'

import { RANGE_PRESETS } from '../InternalAnalyticsPage/websiteAnalyticsModel'

/**
 * The pipeline's date range: the analytics presets (less Today) plus All time, the default
 * (Spencer, 2026-10-13). Shared by Sales → Pipeline and GTM → Channels (VRG-696) so the two
 * offer the same ranges; kept in the URL as `?range=`.
 */
export type PipelineRange = (typeof RANGE_PRESETS)[number]['value'] | 'all'
export const PIPELINE_RANGE_OPTIONS: readonly ViewToggleOption<PipelineRange>[] = [
  ...RANGE_PRESETS.filter((p) => p.value !== 'today').map((p) => ({ value: p.value, label: p.label })),
  { value: 'all', label: 'All time' },
]
export const isPipelineRange = (v: unknown): v is PipelineRange => PIPELINE_RANGE_OPTIONS.some((o) => o.value === v)

export const STAGE_LABEL = SALES_STAGE_LABELS
export const LANE_LABEL = SALES_BOARD_LANE_LABELS

/**
 * VG-27 — the Founder's List pill. The tone is chosen so `founding_member` is unmistakably NOT
 * `member`: that distinction is the whole scarcity story, so a shade of the same colour would not
 * do it. And neither reuses NEUTRAL, which this table already spends twice (Lead source, Commerce
 * platform) — a third grey pill in a third column would read as the same kind of fact.
 *
 *   founding_member  caution (amber)   the gold badge. The scarcest state, and the loudest
 *   member           positive (green)  on the list, a seat in use
 *   invited          info (blue)        offered, nothing signed — pending, not earned
 *   alumni           neutral (grey)     term over; still named, no seat
 *
 * Amber is NOT a warning here, which is the one thing to know before reading it as one: nothing
 * about a founding member needs attention. It is the closest thing to gold the palette has, and
 * a badge is what the canon calls this ("a permanent place on the Vera Founder's List").
 */
export const FOUNDERS_LIST_TONE: Record<FoundersListStatus, 'positive' | 'caution' | 'info' | 'neutral'> = {
  invited: 'info',
  founding_member: 'caution',
  member: 'positive',
  alumni: 'neutral',
}

/** Glossary ids, one per status (§59): the pill and the drawer rows read them. */
export const FOUNDERS_LIST_TERM: Record<FoundersListStatus, string> = {
  invited: 'founders-list-invited',
  founding_member: 'founders-list-founding-member',
  member: 'founders-list-member',
  alumni: 'founders-list-alumni',
}

/** The picker, with each status's "who this is" as the second line. "Not on the list" is a value. */
export const FOUNDERS_LIST_OPTIONS: SelectItem[] = [
  { value: '', label: 'Not on the list' },
  ...FOUNDERS_LIST_STATUSES.map((s) => ({
    value: s,
    label: FOUNDERS_LIST_STATUS_META[s].label,
    description: FOUNDERS_LIST_STATUS_META[s].note,
  })),
]

/** Glossary ids, one per stage and lane (§59): the column headers read them. */
export const LANE_TERM: Record<SalesBoardLane, string> = {
  lead: 'sales-stage-lead',
  access_granted: 'sales-stage-access-granted',
  demo: 'sales-stage-demo',
  account_created: 'sales-stage-account-created',
  paid_client: 'sales-stage-paid-client',
  lost: 'sales-deal-lost',
}

/** Whole units: `$657,588`. */
export const money = (amount: number | null | undefined, currency = 'USD'): string =>
  amount === null || amount === undefined ? '—' : formatMoneyCentsCompact(Math.round(amount * 100), currency)

/** Compact for tiles and cards: `$657.6K`, `$1.2M`. */
export const moneyShort = (amount: number | null | undefined, currency = 'USD'): string => {
  if (amount === null || amount === undefined) return '—'
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: amount >= 1000 ? 1 : 0,
    }).format(amount)
  } catch {
    return money(amount, currency)
  }
}

export const valueLabel = (v: DealValue): string => (v.amount === null ? 'No value' : moneyShort(v.amount, v.currency))

export const basisLabel = (v: DealValue): string | null => (v.basis ? DEAL_VALUE_BASIS_LABELS[v.basis] : null)

export const sourceGroupLabel = (g: LeadSourceGroup): string => LEAD_SOURCE_GROUP_LABELS[g]

/** The card's source line: the group, then the finer origin when there is one. */
export const sourceSummary = (s: SalesLeadSource): string => {
  const group = LEAD_SOURCE_GROUP_LABELS[s.group]
  if (s.detail) return s.detail
  if (s.channel) return `${group} · ${sourceChannelLabel(s.channel)}`
  return group
}

export const daysLabel = (n: number): string => (n === 0 ? 'Today' : n === 1 ? '1 day' : `${n} days`)

/** Stage names the system could not see, for the confirm dialog: "Signed up and Won". */
export const stageList = (stages: readonly SalesStage[]): string => {
  const names = stages.map((s) => SALES_STAGE_LABELS[s])
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** What the system needs to see for a stage, in the user's terms. */
export const EVIDENCE_COPY: Record<Exclude<SalesStage, 'lead'>, string> = {
  access_granted: 'a waitlist grant for one of its people',
  demo: 'a demo provisioned for it',
  account_created: 'a real (non-demo) platform company linked to it',
  // VRG-727 — Won is the FIRST PAID INVOICE: above $0, in Stripe live mode.
  paid_client: "a first paid invoice (live mode, above $0) on its platform company's Stripe account",
}

export const MANAGE_SALES_TIP = "You need the 'Manage sales' permission. Ask an admin to grant it."
/** VRG-723 — Create demo is gated on the Demos permission (§46: disabled, named). */
export const CREATE_DEMO_TIP = "You need the 'Provision demos' permission. Ask an admin to grant it."

// ── VRG-701: several deals per company ───────────────────────────────────────────────────

/** "Maison Vera · Rollout": the company, then the deal's own name when it has one. */
export const dealTitle = (d: Pick<SalesDealCard, 'account_name' | 'name'>): string => (d.name ? `${d.account_name} · ${d.name}` : d.account_name)

/** The SelectField value for "inherit the company's first-touch source". */
export const INHERIT_SOURCE = ''

/**
 * A deal's source picker: inherit (the default for a company's first deal), Expansion (the default
 * for a later deal at a client), or a channel by hand.
 */
export const DEAL_SOURCE_OPTIONS: SelectItem[] = [
  { value: INHERIT_SOURCE, label: "Company's first touch" },
  { value: EXPANSION_SOURCE, label: 'Expansion (existing client)' },
  ...SOURCE_CHANNEL_OPTIONS,
]

/** The plan picker: the catalog's tiers by their stable key (VRG-365). */
export const PLAN_OPTIONS: SelectItem[] = [{ value: '', label: 'Not set' }, ...DEAL_PLANS.map((p) => ({ value: p, label: DEAL_PLAN_LABELS[p] }))]

export const planLabel = (p: string | null | undefined): string => (p && p in DEAL_PLAN_LABELS ? DEAL_PLAN_LABELS[p as keyof typeof DEAL_PLAN_LABELS] : 'Not set')

/** Copy shared by the Add deal form and the drawer: why a second open deal is only a warning. */
/**
 * VRG-701 — Enterprise is custom-priced (contact sales, no list price), so a deal on it, or a
 * Tier 1–2 deal priced by the tier's default plan, is priced as Scale and shown as "at least"
 * that figure (Spencer, 2026-10-14). One sentence, every place a floor is shown.
 */
export const ENTERPRISE_FLOOR_TIP =
  'Enterprise is custom-priced: it has no list price. This is priced as Scale, the highest listed plan, so the real figure is at least this.'
export const atLeast = (floor: boolean, text: string): string => (floor ? `at least ${text}` : text)

export const OPEN_DEAL_WARNING =
  'This company already has an open deal. Add this one only if it is a separate sale (an upsell, a rollout); otherwise edit the existing deal.'
